import React, { useState, useEffect } from "react"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import PageContent from "components/common/LayoutComponents/PageContent"
import Button from "@mui/material/Button"
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded"
import LoadingScreen from "components/common/LoadingScreen"
import { verifyResponse } from "utils/httpRequest"
import { getSystemConfigs } from "services/systemConfig"
import { UsePermissionVerification } from "components/customHooks"
import { PERMISSIONS } from "constants/permission"
import Editor from "@monaco-editor/react"
import { useSnackbar } from "notistack"
import SystemConfigDlg from "./SystemConfigDlg"
import { SystemConfigWrapper } from "./SystemConfig.style"

const SystemConfig = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [configs, setConfigs] = useState([])
  const [loading, setLoading] = useState(false)
  const [displayConfig, setDisplayConfig] = useState(null)

  const [
    openSystemConfigDialog,
    systemConfigDialog,
    systemConfigDialogLoading,
  ] = SystemConfigDlg({
    onSubmit: () => {
      loadConfigs()
    },
  })

  const AccessControl = UsePermissionVerification()

  const loadConfigs = async () => {
    setLoading(true)
    try {
      const response = await getSystemConfigs()
      verifyResponse(response)
      const configList = response.data || []
      setConfigs(configList)
      setDisplayConfig((prevConfig) => {
        if (!configList.length) return null
        if (!prevConfig) return configList[0]
        return (
          configList.find(
            (item) =>
              item.id === prevConfig.id ||
              item.configKey === prevConfig.configKey
          ) || configList[0]
        )
      })
    } catch {
      enqueueSnackbar("Failed to load system configs.", { variant: "warning" })
      setConfigs([])
      setDisplayConfig(null)
    } finally {
      setLoading(false)
    }
  }

  const CodeDisplay = ({ access }) => {
    return displayConfig ? (
      <>
        <div className="display-config-header">
          <div className="heading">Json Editor</div>
          {access.editSystemConfig && (
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                openSystemConfigDialog(displayConfig)
              }}
            >
              Edit
            </Button>
          )}
        </div>
        <div className="display-code-info">
          <div className="name">
            <div className="label-text">Name</div>
            {displayConfig.name}
          </div>
          <div className="key">
            <div className="label-text">Key</div>
            {displayConfig.configKey}
          </div>
        </div>
        <Editor
          className="json-editor"
          theme="vs-dark"
          height="calc(100vh - 390px)"
          defaultLanguage="json"
          value={displayConfig.configValue || ""}
          options={{
            readOnly: true,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
          }}
        />
      </>
    ) : (
      <div className="display-placeholder">
        Select a configuration from the list to preview its JSON value.
      </div>
    )
  }

  useEffect(() => {
    loadConfigs()
  }, [])

  useEffect(() => {
    AccessControl.SetAccess({
      ...AccessControl.Access,
      listSystemConfig: AccessControl.VerifyAccess([
        PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_LIST,
      ]),
      addSystemConfig: AccessControl.VerifyAccess([
        PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_ADD,
      ]),
      editSystemConfig: AccessControl.VerifyAccess([
        PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_EDIT,
      ]),
      deleteSystemConfig: AccessControl.VerifyAccess([
        PERMISSIONS.SYSTEM_CONFIG.SYSTEM_CONFIG_DELETE,
      ]),
    })
  }, [AccessControl.UserPermissions])

  return (
    <PageContent>
      <SystemConfigWrapper>
        <PageHeading>
          <div className="heading-copy">
            <h1 className="page-title">System Configurations</h1>
            <p className="page-subtitle">
              Manage JSON-based platform configuration keys and values.
            </p>
          </div>
          <div className="actions">
            {AccessControl.Access.addSystemConfig && (
              <Button
                className="new-config-btn"
                variant="contained"
                size="medium"
                startIcon={<AddCircleOutlineRoundedIcon />}
                onClick={() => {
                  openSystemConfigDialog()
                }}
              >
                Add System Config
              </Button>
            )}
          </div>
        </PageHeading>
        <div className="page-surface">
          {AccessControl.Access?.listSystemConfig && (
            <div className="config-panel">
              <div className="config-list">
                {configs.map((config) => (
                  <div
                    key={`config-item-${config.id || config.configKey}`}
                    className={`config-item ${
                      displayConfig &&
                      displayConfig.configKey === config.configKey &&
                      "selected"
                    }`}
                    onClick={() => {
                      setDisplayConfig(config)
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault()
                        setDisplayConfig(config)
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="info">
                      <div className="name">{config.name}</div>
                      <div className="key">{config.configKey}</div>
                    </div>
                  </div>
                ))}
                {!configs.length && (
                  <div className="config-list-empty">
                    No system configs found.
                  </div>
                )}
              </div>
              <div className="config-value-display">
                <CodeDisplay access={AccessControl.Access} />
              </div>
            </div>
          )}
          {!AccessControl.Access?.listSystemConfig && (
            <div className="page-empty">
              You do not have permission to view system configs.
            </div>
          )}
        </div>
      </SystemConfigWrapper>
      <LoadingScreen loading={loading || systemConfigDialogLoading} />
      {systemConfigDialog}
    </PageContent>
  )
}

export default SystemConfig
